import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Typography, Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { TableView } from "../components/TableView";
import { alertActions } from "_store";
import { ModalPopup } from "_components";
import { Filter } from "../components/Filter";
import {
  getSupplierDiversitySummaryCountApi,
  postExpiringSupplierApi,
  postRegisteredSupplierFilterApi,
  postSupplierDiversityExpiringInApi,
} from "services/supplierDiversity/supplierDiversity.thunks";
import { ERR_MSG } from "constants/labels";
import ReportsDownload from "../components/ReportsDownload";

export const SupplierList = () => {
  const header = " Supplier Development";
  const dispatch = useDispatch();

  const [supplierData, setSupplierData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [openComponent, setOpenComponent] = useState(null); // State to track which component is open
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [summaryCount, setSummaryCount] = useState([]);
  const [expiringData, setExpiringData] = useState([]);
  const [expiringInOptions, setExpiringInOptions] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [expiringIn, setExpiringIn] = useState([]);
  const [activeContainer, setActiveContainer] = useState("regSuppliers");
  const [registeredCount, setRegisteredCount] = useState(0);
  const [downloadPayload, setDownloadPayload] = useState({
    RegisteredFrom: null,
    RegisteredTo: null,
    CompanyName: null,
    StateId: null,
    CategoryId: null,
  });
  const handleError = (error) => {
    dispatch(alertActions.clear());
    dispatch(
      alertActions.error({
        message: error.message ? ERR_MSG["073"] : error,
        header: header,
      })
    );
  };
  const filterPayload = {
    RegisteredFrom: null,
    RegisteredTo: null,
    CompanyName: null,
    StateId: null,
    CategoryId: null,
  };

  const fetchData = async () => {
    try {
      const res = await dispatch(
        postRegisteredSupplierFilterApi(filterPayload)
      ).unwrap();
      const { Data, Succeeded, Message } = res;

      setSupplierData(Succeeded ? Data : []);
      if (!Succeeded) handleError(Message);
    } catch (error) {
      setSupplierData([]);
      handleError(error);
    }
  };

  const expiringPayload = {
    RegisteredFrom: null,
    RegisteredTo: null,
    CompanyName: null,
    StateId: null,
    CategoryId: null,
    ExpiringIn: null,
  };

  const fetchSupplierExpiringData = async () => {
    try {
      const res = await dispatch(
        postExpiringSupplierApi(expiringPayload)
      ).unwrap();
      const { Data = [], Succeeded = false, Message = "" } = res || {};

      setExpiringData(Succeeded ? Data : []);
      if (!Succeeded) handleError(Message);
    } catch (error) {
      setExpiringData([]);
      handleError(error);
    }
  };

  const fetchSuppliersSummaryCount = async () => {
    try {
      const res = await dispatch(
        getSupplierDiversitySummaryCountApi()
      ).unwrap();
      const { Data, Succeeded, Message } = res;

      setRegisteredCount(Succeeded ? Data?.RegisteredSupplier : 0);
      setSummaryCount(Succeeded ? Data?.NewSupplier : 0);
      const expiringValues = [
        Data?.CertificateExpiring30 ?? 0,
        Data?.CertificateExpiring60 ?? 0,
        Data?.CertificateExpiring90 ?? 0,
      ];

      setExpiringIn(expiringValues);
      if (!Succeeded) handleError(Message);
      return;
    } catch (error) {
      setSupplierData([]);
      handleError(error);
    }
  };

  const fetchSupplierExpiringIn = async () => {
    try {
      const res = await dispatch(postSupplierDiversityExpiringInApi()).unwrap();
      const { Data, Succeeded, Message } = res;
      setExpiringInOptions(Succeeded && Array.isArray(Data) ? Data : []);
      if (!Succeeded) handleError(Message);
    } catch (error) {
      setExpiringIn([]);
      handleError(error);
    }
  };

  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);

  const newSupplierPayload = {
    RegisteredFrom: startOfYear,
    RegisteredTo: today,
    CompanyName: null,
    StateId: null,
    CategoryId: null,
    ExpiringIn: null,
  };
  const fetchNewSupplierWithinAyear = async () => {
    try {
      const res = await dispatch(
        postRegisteredSupplierFilterApi(newSupplierPayload)
      ).unwrap();
      const { Data, Succeeded, Message } = res;
      setSupplierData(Succeeded ? Data : []);
      if (!Succeeded) handleError(Message);
    } catch (error) {
      setExpiringIn([]);
      handleError(error);
    }
  };
  const loadData = async () => {
    await Promise.all([
      fetchData(),
      fetchSuppliersSummaryCount(),
      fetchSupplierExpiringIn(),
    ]);
  };
  useEffect(() => {
    if (!hasLoaded) {
      loadData().then(() => setHasLoaded(true));
    }
  }, []);

  const handleRegSuppliersClick = async () => {
    setActiveContainer("regSuppliers"); // Mark as active
    fetchData();
    fetchSuppliersSummaryCount();
  };

  const handleNewSuppliersClick = async () => {
    setActiveContainer("newSuppliers");
    fetchNewSupplierWithinAyear();
    fetchSuppliersSummaryCount();
  };
  const handleExpiringSuppliersClick = async () => {
    setActiveContainer("expiringIN"); // Mark as active
    fetchSupplierExpiringData();
    fetchSupplierExpiringIn();
  };

  const handleFilterSubmit = async (data) => {
    if (data.length > 0) {
      activeContainer === "expiringIN"
        ? setExpiringData(data)
        : setSupplierData(data);
    } else {
      dispatch(
        alertActions.error({
          message: "No results found for the provided search criteria.",
          header: header,
        })
      );
    }
  };

  const handleToggleActiveStatus = () => {
    const inActive = selectedRows.some((row) => !row.IsActive);
    if (inActive) {
      dispatch(
        alertActions.error({
          message: "Please deselect inactive supplier diversity to proceed",
          header: header,
        })
      );
    } else {
      setConfirmDialogOpen(true);
    }
  };

  const handleOpenComponent = (component) => {
    setOpenComponent((prev) => (prev === component ? null : component));
    setBackdropOpen((prev) => (prev === component ? false : true)); // Toggle backdrop
  };

  const handleCloseBackdrop = () => {
    setBackdropOpen(false);
    setOpenComponent(null);
  };

  return (
    <>
      <Typography component="div" className="userprofilelist ">
        <Grid container direction="row" spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <Typography variant="h2" className="userprofilelistcontent">
              Supplier Development
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Grid
              container
              spacing={2}
              justifyContent="flex-end"
              className="SupplierDiversityManagement"
            >
              <Grid size={{ xs: 12, sm: 12, md: 8 }}>
                <Grid container spacing={2} justifyContent="flex-end">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: { xs: "flex-start", sm: "flex-end" }, // right-align
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: "24px", // spacing between the two buttons
                      width: "100%",
                    }}
                  >
                    <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                      <Filter
                        handleFilterSubmit={handleFilterSubmit}
                        isOpen={openComponent === "filter"}
                        onClose={handleCloseBackdrop}
                        onOpen={() => handleOpenComponent("filter")}
                        activeView={activeContainer}
                        expiringInData={expiringInOptions}
                        setDownloadPayload={setDownloadPayload}
                      />
                    </Box>

                    <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
                      <ReportsDownload
                        expiringInData={expiringData}
                        supplierData={supplierData}
                        type={activeContainer}
                        hasFilter={downloadPayload}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Typography>
      <Box className="SuplierDiversity">
        <Grid container spacing={2}>
          <Grid
            size={{ xs: 12, sm: 6, md: 3 }}
            className={`SuplierDiversitycontainer ${
              activeContainer === "regSuppliers" ? "active" : ""
            }`}
            onClick={handleRegSuppliersClick}
            id="regSuppliers"
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === "Enter" && handleRegSuppliersClick()}
          >
            <Grid container id="regSuppliers">
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <Typography component="h3">Registered Suppliers</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <Typography component="h4">{registeredCount}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 6, md: 3 }}
            className={`SuplierDiversitycontainer ${
              activeContainer === "newSuppliers" ? "active" : ""
            }`}
            onClick={handleNewSuppliersClick}
            id="newSuppliers"
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === "Enter" && handleNewSuppliersClick()}
          >
            <Grid container>
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <Typography component="h3">New Suppliers</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <Typography component="h4">{summaryCount}</Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            size={{ xs: 12, sm: 12, md: 6 }}
            className={`SuplierDiversitycontainer ${
              activeContainer === "expiringIN" ? "active" : ""
            }`}
            onClick={handleExpiringSuppliersClick}
            id="expiringIN"
            role="button"
            tabIndex={0}
            onKeyPress={(e) =>
              e.key === "Enter" && handleExpiringSuppliersClick()
            }
          >
            <Grid container>
              <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                <Typography component="h3">
                  certifications expiring in
                </Typography>
              </Grid>
              <Grid
                size={{ xs: 12, sm: 8, md: 8 }}
                className="SuplierDiversitycertifications"
              >
                <Grid container>
                  <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                    <Typography>30 days</Typography>
                    <Typography component="h4">
                      {expiringIn?.[0] ?? 0}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                    <Typography>60 days</Typography>
                    <Typography component="h4">
                      {expiringIn?.[1] ?? 0}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                    <Typography>90 days</Typography>
                    <Typography component="h4">
                      {expiringIn?.[2] ?? 0}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <div className={backdropOpen ? "backdrop" : ""}></div>
      <div className="SupplierList SupplierDcontainer">
        <TableView
          supplierData={
            activeContainer === "expiringIN" ? expiringData : supplierData
          }
          rowSelection={rowSelection}
          setSelectedRows={setSelectedRows}
          setRowSelection={setRowSelection}
          activeView={activeContainer}
          clearFilter={
            activeContainer === "regSuppliers"
              ? handleRegSuppliersClick
              : activeContainer === "newSuppliers"
              ? handleNewSuppliersClick
              : activeContainer === "expiringIN"
              ? handleExpiringSuppliersClick
              : null
          }
        />
      </div>

      {confirmDialogOpen && (
        <ModalPopup
          header="Supplier Diversity"
          message1="Are you sure you want to deactivate selected Supplier?"
          btnPrimaryText="Confirm"
          btnSecondaryText="Cancel"
          // handlePrimaryClick={() => handleConfirmDeactivation()}
          handleSecondaryClick={() => setConfirmDialogOpen(false)}
        />
      )}
    </>
  );
};
